import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  getChatDetail,
  getWhatsAppErrorMessage,
} from "../../../services/whatsappApi";
import ResponseWindowCard from "./ResponseWindowCard";
import ContactInfoCard from "./ContactInfoCard";
import ChatNotesPanel from "./ChatNotesPanel";
import MediaAssetsPanel from "./MediaAssetsPanel";

function SidebarSkeleton() {
  return (
    <div className="wa-sidebar-body wa-crm-sidebar-body">
      {[1, 2, 3, 4].map((k) => (
        <div
          key={k}
          className="whatsapp-skeleton"
          style={{ height: 88, marginBottom: 12, borderRadius: 12 }}
        />
      ))}
    </div>
  );
}

export default function ChatRightSidebar({
  chatId,
  open,
  onClose,
  onResponseWindowChange,
  refreshToken = 0,
  onImagePreview,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadDetail = useCallback(
    async ({ silent = false } = {}) => {
      if (!chatId) {
        setDetail(null);
        return;
      }
      if (!silent) {
        setLoading(true);
        setError("");
      }
      try {
        const res = await getChatDetail(chatId);
        const data = res?.data ?? res;
        setDetail(data);
        if (typeof onResponseWindowChange === "function") {
          onResponseWindowChange(data?.responseWindow || null, {
            sessionExpired: data?.responseWindow?.isExpired === true,
          });
        }
      } catch (err) {
        const msg = getWhatsAppErrorMessage(err, "Failed to load chat details");
        if (!silent) {
          setError(msg);
          toast.error(msg);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [chatId, onResponseWindowChange],
  );

  useEffect(() => {
    loadDetail();
  }, [loadDetail, refreshToken]);

  const handleNotesChange = (notesOrNull) => {
    if (notesOrNull === null) {
      loadDetail({ silent: true });
      return;
    }
    setDetail((prev) => (prev ? { ...prev, chatNotes: notesOrNull } : prev));
  };

  const handleContactSaved = () => {
    loadDetail({ silent: true });
  };

  const handleWindowExpired = useCallback(() => {
    if (typeof onResponseWindowChange === "function") {
      onResponseWindowChange(
        {
          ...(detail?.responseWindow || {}),
          isExpired: true,
          remainingSeconds: 0,
        },
        { sessionExpired: true },
      );
    }
    loadDetail({ silent: true });
  }, [detail?.responseWindow, loadDetail, onResponseWindowChange]);

  if (!chatId) return null;

  return (
    <aside
      className={`whatsapp-right-sidebar wa-crm-sidebar ${open ? "open" : ""}`}
      aria-label="Chat details"
    >
      <button
        type="button"
        className="whatsapp-icon-btn wa-sidebar-close"
        onClick={onClose}
        aria-label="Close details"
      >
        <Icon icon="solar:close-circle-line-duotone" width={22} />
      </button>

      {loading ? (
        <SidebarSkeleton />
      ) : error ? (
        <div className="wa-sidebar-body wa-crm-sidebar-body">
          <div className="whatsapp-list-error">{error}</div>
          <button
            type="button"
            className="whatsapp-btn-secondary whatsapp-btn-sm mt-2"
            onClick={() => loadDetail()}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="wa-sidebar-body wa-crm-sidebar-body">
          {/* Mockup order: Chat note → Contact → Response window → Media */}
          <ChatNotesPanel
            chatId={chatId}
            chatNotes={detail?.chatNotes || []}
            onNotesChange={handleNotesChange}
          />
          <ContactInfoCard
            chatId={chatId}
            senderName={detail?.senderName}
            senderMobile={detail?.senderMobile || detail?.chatId}
            contact={detail?.contact}
            onContactSaved={handleContactSaved}
          />
          <ResponseWindowCard
            responseWindow={detail?.responseWindow}
            onExpired={handleWindowExpired}
          />
          <MediaAssetsPanel
            chatId={chatId}
            mediaCounts={detail?.mediaCounts}
            onImagePreview={onImagePreview}
          />
        </div>
      )}
    </aside>
  );
}
